// Null-aware metric rendering. ONE definition, imported by every Analytics page.
//
// Why this module exists. Analytics had two helpers named `toMoney` in two
// files that behaved OPPOSITELY on the same input: AnalyticsDashboard's
// returned "—" for a missing value, AnalyticsLeadConversion's returned "$0".
// Alongside them, `Number(x || 0)` appeared throughout, turning every absent
// number into a real-looking zero — a missing revenue day read as $0 of
// revenue, a missing load rate as 0% utilization, and a rep with no reported
// close rate as a 0% bar beside their name.
//
// The rule: a missing number is UNKNOWN, and unknown renders "—", never 0.
// A reported 0 is a real answer and is preserved as 0.

/** A finite number, or null for anything absent/blank/unparseable. */
export const num = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/** Money, or "—" when unknown. A real 0 renders "$0". */
export const toMoney = (value) => {
  const n = num(value);
  if (n === null) return "—";
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

/** A 0..1 ratio as a percentage, or "—" when unknown. */
export const safePct = (ratio) => {
  const n = num(ratio);
  if (n === null) return "—";
  return `${Math.round(n * 1000) / 10}%`;
};

/** Percentage change, or null when it cannot honestly be computed. */
export const ratioChange = (current, previous) => {
  const a = num(current);
  const b = num(previous);
  if (a === null || b === null || b === 0) return null;
  return (a - b) / Math.abs(b);
};

/**
 * Sum a window of rows, SKIPPING entries whose value is unknown, and report
 * that it skipped them.
 *
 * Coercing a missing day to 0 and summing it made absent data look like a
 * decline — a fake number driving a judgement. A window with any missing entry
 * is `partial`; a window with nothing known totals null, not 0.
 */
export const sumKnown = (rows, read) => {
  const list = Array.isArray(rows) ? rows : [];
  const known = list.map((r) => num(read(r))).filter((v) => v !== null);
  return {
    total: known.length ? known.reduce((acc, v) => acc + v, 0) : null,
    known: known.length,
    of: list.length,
    missing: list.length - known.length,
    partial: known.length < list.length,
  };
};
